import { useMemo, useState } from "react";
import { Blocks, BriefcaseBusiness, Building2, Cpu, HardDrive, Route, Shield, Sparkles, Users } from "lucide-react";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { AdminCustomersPanel } from "@/components/aion/AdminCustomersPanel";
import { AdminEmployeesPanel } from "@/components/aion/AdminEmployeesPanel";
import { AdminHardwarePanel } from "@/components/aion/AdminHardwarePanel";
import { AdminInstallationsPanel } from "@/components/aion/AdminInstallationsPanel";
import { AdminPhase15Panel } from "@/components/aion/AdminPhase15Panel";
import { AdminSystemPanel } from "@/components/aion/AdminSystemPanel";
import { AdminUsersPanel } from "@/components/aion/AdminUsersPanel";
import { AdminAIProviderPanel } from "@/components/aion/AdminAIProviderPanel";
import { InstallerOnboardingPanel } from "@/components/aion/InstallerOnboardingPanel";
import { OperatorHomePanel } from "@/components/aion/OperatorHomePanel";

const baseTabs = [
  { id: "dashboard", label: "HOME", icon: Blocks },
  { id: "customers", label: "CUSTOMERS", icon: Building2 },
  { id: "onboarding", label: "ONBOARDING", icon: Route },
  { id: "installations", label: "INSTALLATIONS", icon: Shield },
  { id: "hardware", label: "HARDWARE", icon: HardDrive },
  { id: "phase15", label: "PHASE 15", icon: Shield, roles: ["PLATFORM_ADMIN", "MANAGER", "INSTALLER", "TECHNICIAN", "SUPPORT"] },
  { id: "users", label: "USERS", icon: Users },
  { id: "employees", label: "EMPLOYEES", icon: BriefcaseBusiness, roles: ["PLATFORM_ADMIN", "MANAGER"] },
  { id: "ai-voice", label: "AI & VOICE", icon: Sparkles, roles: ["PLATFORM_ADMIN"] },
  { id: "system", label: "SYSTEM", icon: Cpu },
];

export const AionAdminWorkspace = ({ customers = [], dashboard = null, employees = [], hardware = [], home = null, installations = [], operatorRole = "PLATFORM_ADMIN", roles = [], systemSummary = null, token, users = [], onRefresh }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const visibleTabs = useMemo(() => baseTabs.filter((tab) => !tab.roles || tab.roles.includes(operatorRole)), [operatorRole]);
  const counts = useMemo(() => ({
    customers: customers.length,
    installations: installations.length,
    hardware: hardware.length,
    users: users.length,
    employees: employees.length,
    online: hardware.filter((item) => item.online).length,
  }), [customers, employees.length, hardware, installations.length, users.length]);
  const scopeLabel = ["CUSTOMER_OWNER", "CUSTOMER_ADMIN"].includes(operatorRole) ? "Tenant-scoped" : "Assigned customer scope";

  return (
    <div className="aion-view aion-admin-stack" data-testid="aion-admin-workspace">
      <AionSectionCard
        title="AION Admin"
        eyebrow="CONTROL PLANE"
        subtitle="Kunden, Installationen, Hardware, Nutzer und Systemzustand laufen in einem separaten Betreiberbereich unter MORE → ADMIN zusammen."
        testId="aion-admin-workspace-card"
      >
        <div className="aion-compact-status-grid" data-testid="aion-admin-workspace-stats">
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-customers"><span>Customers</span><strong>{counts.customers}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-installations"><span>Installations</span><strong>{counts.installations}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-hardware"><span>Hardware</span><strong>{counts.hardware}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-users"><span>Users</span><strong>{counts.users}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-employees"><span>Employees</span><strong>{counts.employees}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-online"><span>Online</span><strong>{counts.online}</strong></div>
          <div className="aion-compact-status-box" data-testid="aion-admin-stat-scope"><span>Access</span><strong>{scopeLabel}</strong></div>
        </div>
        <div className="aion-section-nav" data-testid="aion-admin-nav">
          {visibleTabs.map(({ id, label, icon: Icon }) => (
            <button
              type="button"
              key={id}
              className={`aion-section-nav-item${activeTab === id ? " aion-section-nav-item-active" : ""}`}
              onClick={() => setActiveTab(id)}
              data-testid={`aion-admin-tab-${id}`}
            >
              <Icon className="aion-button-icon" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </AionSectionCard>

      {activeTab === "dashboard" ? <OperatorHomePanel dashboard={dashboard} hardware={hardware} home={home} installations={installations} operatorRole={operatorRole} onRefresh={onRefresh} onSelectTab={setActiveTab} /> : null}
      {activeTab === "customers" ? <AdminCustomersPanel customers={customers} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "onboarding" ? <InstallerOnboardingPanel customers={customers} operatorRole={operatorRole} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "installations" ? <AdminInstallationsPanel customers={customers} installations={installations} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "hardware" ? <AdminHardwarePanel customers={customers} hardware={hardware} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "phase15" ? <AdminPhase15Panel installations={installations} systemSummary={systemSummary} operatorRole={operatorRole} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "users" ? <AdminUsersPanel customers={customers} roles={roles} token={token} users={users} onRefresh={onRefresh} /> : null}
      {activeTab === "employees" ? <AdminEmployeesPanel customers={customers} employees={employees} installations={installations} token={token} onRefresh={onRefresh} /> : null}
      {activeTab === "ai-voice" ? <AdminAIProviderPanel token={token} /> : null}
      {activeTab === "system" ? <AdminSystemPanel customers={customers} hardware={hardware} installations={installations} systemSummary={systemSummary} token={token} users={users} onRefresh={onRefresh} /> : null}
    </div>
  );
};